package waldo.sankan.lineamunicipalkmp.model

import androidx.compose.runtime.Stable
import androidx.compose.ui.input.pointer.PointerId
import kotlinx.serialization.Serializable

interface HistoricoRepository {
    fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int
}

@Serializable
@Stable
data class GeoPoint(
    val latitude: Double = 0.0,
    val longitude: Double = 0.0
)

@Serializable
@Stable
data class Noticia(
    var id: String = "",
    var titulo: String = "",
    var contenido: String = "",
    var imagenUrl: String? = null,
    var comunaRef: String? = null,
    var comunaNombre: String? = null,
    var autor: String? = null,
    var fechaCreacion: Long? = null,
    // Nuevos campos para segmentación
    var sectorRef: String? = null,
    var sectorDisplayName: String? = null,
    var edadMinima: Int? = null,
    var edadMaxima: Int? = null,
    var categoria: String? = "GENERAL" // --- NUEVO CAMPO: ALERTA, EVENTO, GESTION, SEGURIDAD ---
)

@Serializable
@Stable
data class UsuarioCiudadano(
    var uid: String = "",
    var nombre: String? = null,
    var rut: String? = null,
    var correoElectronico: String? = null,
    var numeroCelular: String? = null,
    var fechaNacimiento: Long? = null,
    var comunaRef: String? = null,
    var comunaDisplayName: String? = null,
    var sectorRef: String? = null,
    var sectorDisplayName: String? = null,
    var regionRef: String? = null,
    var regionDisplayName: String? = null,
    var tokenFCM: String? = null,
    var fechaCreacion: Long? = null,
    var ultimoAcceso: Long? = null,
    var esAdministrador: Boolean = false
)

@Serializable
@Stable
data class UsuarioSolucionador(
    var nombre: String? = null,
    var correoElectronico: String? = null,
    var rut: String? = null,
    var email: String? = null,
    var uidAuth: String? = null,
    var comunaAsignadaRef: String? = null,
    var comunaAsignadaDisplayName: String? = null,
    var tipoIncidenciaAsignadoRef: String? = null,
    var tipoIncidenciaAsignadoDisplayName: String? = null,
    var sectorAsignado: String? = null,
    var tokenFCM: String? = null,
    var estaActivo: Boolean = true,
    var fechaCreacion: Long? = null
)

@Serializable
@Stable
data class Incidencia(
    var id: String = "",
    var usuarioRef: String? = null,
    var usuarioNombre: String? = null,
    var usuarioRut: String? = null,
    var tipoIncidenciaRef: String? = null,
    var tipoIncidenciaDisplayName: String? = null,
    var comunaRef: String? = null,
    var comunaDisplayName: String? = null,
    var regionRef: String? = null,
    var regionDisplayName: String? = null,
    var descripcion: String = "",
    var estado: String = "EN_PROCESO",
    var ubicacionTexto: String? = null,
    var direccion: String? = null,
    var ubicacionGPS: GeoPoint? = null,
    var urlImagen: String? = null,
    var fotoUri: String? = null,
    var observacionSolucionador: String? = null,
    var idUsuarioSolucionadorAsignado: String? = null,
    var nombreSolucionadorAsignado: String? = null,
    val idUsuariosRefuerzo: List<String> = emptyList(),
    var sectorIncidencia: String? = null,
    var urlImagenSolucion: String? = null,
    var urlImagenVerificacionDron: String? = null,
    var ubicacionGPSSolucion: GeoPoint? = null,
    var fechaCreacion: Long? = null,
    var fechaActualizacion: Long? = null,
    var historialEstados: List<CambioEstadoIncidencia> = emptyList(),
    var comentarios: List<ComentarioIncidencia> = emptyList(),
    var fechaSolucion: Long? = null,
    var tipificacionesDelito: List<String> = emptyList(),
    var huboDetenidos: Boolean = false,
    var duracionSolucionMs: Long? = null,
    var cantidadDetenidos: Int = 0,
    var cantidadFiscalizaciones: Int = 0,
    var cantidadControlesIdentidad: Int = 0,
    var origenReporte: String = "APP",
    var creadoPorNombre: String? = null,
    var creadoPorId: String? = null,
    var idComunaReceptora: String? = null,
    var idTipoIncidenciaReceptora: String? = null,
    var notificarA: String? = null,
    var notificarA2: String? = null,
    var notificarA3: String? = null,
    var marcadoComoDuplicado: Boolean = false,
    var padreId: String? = null,
    var reportesSimilaresCount: Int = 0,
    var observacionAuditoria: String? = null
)

@Serializable
@Stable
data class Sector(
    var id: String = "",
    var nombre: String = "",
    var estaActivo: Boolean = true,
    var tipoRef: String = "",
    var tipoDisplay: String = "",
    var vinculoId: String? = null, // "DAO_SHARED", ID_SEGURIDAD o ID_SALVAVIDAS
    var comunaAsignadaRef: String? = null
)

@Serializable
@Stable
data class CambioEstadoIncidencia(
    var estado: String = "",
    var fecha: Long? = null,
    var usuarioRef: String? = null,
    var usuarioNombre: String? = null,
    var rolUsuario: String? = null
)

@Serializable
@Stable
data class ComentarioIncidencia(
    var texto: String = "",
    var fecha: Long? = null,
    var usuarioRef: String? = null,
    var usuarioNombre: String? = null,
    var rolUsuario: String? = null
)

@Serializable
@Stable
data class TipoIncidencia(
    var id: String = "",
    var nombre: String = "",
    var nombreDisplay: String? = null,
    var descripcion: String? = null,
    var iconoLocalRef: String? = null,
    var orden: Int = 0,
    var estaActivo: Boolean = true,
    var requiereMultiplesSolucionadores: Boolean = false,
    var comunaAsignadaRef: String? = null,
    var comunaAsignadaDisplayName: String? = null,
    var departamento: String? = null // Ej: "DAO", "SEGURIDAD", "SOCIAL", "TURISMO"
)

@Serializable
@Stable
data class Region(
    var id: String = "",
    var nombre: String = "",
    var orden: Int = 0,
    var estaActiva: Boolean = true
)

@Serializable
@Stable
data class Comuna(
    var id: String = "",
    var comunaDisplayName: String = "",
    var estaActiva: Boolean = true,
    var noticiasHabilitadas: Boolean = false,
    var noticiasMensualesEnviadas: Int = 0,
    var noticiasMensualesLimite: Int = 4,
    var fechaUltimoReinicioNoticias: Long? = null,
    var regionRef: String? = null,
    var regionDisplayName: String? = null
)

@Serializable
@Stable
data class Administrador(
    var uidAuth: String = "",
    var nombre: String? = null,
    var email: String? = null,
    var rol: String = "COMUNAL",
    var uidRol: String? = null,
    var comunaAsignadaRef: String? = null,
    var comunaAsignadaDisplayName: String? = null,
    var tokenFCM: String? = null,
    var estaActivo: Boolean = true,
    var fechaCreacion: Long? = null
)

@Serializable
@Stable
data class DelitoTipificado(
    val nombre: String,
    val grupo: String,
    val familia: String,
    val colorHex: String,
    val estaActivo: Boolean = true,
    val tipoIncidenciaRef: String? = null,
    val comunaAsignadaRef: String? = null,
    val comunaAsignadaDisplayName: String? = null
)

object TipificacionesRepositoryCEAD {
    val listaMaestraDeDelitos = listOf(
        DelitoTipificado(nombre = "Homicidios", grupo = "Homicidios y femicidios", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Femicidios", grupo = "Homicidios y femicidios", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Violaciones", grupo = "Violaciones y otros delitos sexuales", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Abusos sexuales", grupo = "Violaciones y otros delitos sexuales", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Acosos sexuales", grupo = "Violaciones y otros delitos sexuales", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#D32F2F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otros delitos sexuales", grupo = "Violaciones y otros delitos sexuales", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#D32F2F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Lesiones graves o gravísimas", grupo = "Lesiones graves o gravísimas", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#D32F2F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Lesiones menos graves", grupo = "Lesiones menos graves", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#E53935", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Lesiones leves", grupo = "Lesiones leves", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#E53935", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Amenazas", grupo = "Amenazas", familia = "Delitos contra la vida o integridad de las personas", colorHex = "#E53935", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robos con violencia o intimidación", grupo = "Robos con violencia o intimidación", familia = "Robos violentos", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo violentos de vehículo motorizado", grupo = "Robos con violencia o intimidación", familia = "Robos violentos", colorHex = "#B71C1C", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo por sorpresa", grupo = "Robo por sorpresa", familia = "Robos violentos", colorHex = "#D32F2F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Violencia intrafamiliar con lesiones físicas", grupo = "Violencia intrafamiliar", familia = "Violencia intrafamiliar", colorHex = "#6A1B9A", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Violencia intrafamiliar con lesiones psicológicas", grupo = "Violencia intrafamiliar", familia = "Violencia intrafamiliar", colorHex = "#6A1B9A", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Maltrato habitual", grupo = "Violencia intrafamiliar", familia = "Violencia intrafamiliar", colorHex = "#7B1FA2", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Amenazas en contexto de violencia intrafamiliar", grupo = "Violencia intrafamiliar", familia = "Violencia intrafamiliar", colorHex = "#7B1FA2", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Violencia intrafamiliar no clasificada", grupo = "Violencia intrafamiliar", familia = "Violencia intrafamiliar", colorHex = "#8E24AA", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Tráfico de sustancias", grupo = "Crímenes y simples delitos ley de drogas", familia = "Delitos asociados a drogas", colorHex = "#455A64", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Microtráfico de sustancias", grupo = "Crímenes y simples delitos ley de drogas", familia = "Delitos asociados a drogas", colorHex = "#455A64", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Elaboración o producción de sustancias", grupo = "Crímenes y simples delitos ley de drogas", familia = "Delitos asociados a drogas", colorHex = "#37474F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otras infracciones a la ley de drogas", grupo = "Crímenes y simples delitos ley de drogas", familia = "Delitos asociados a drogas", colorHex = "#546E7A", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Consumo de drogas en la vía pública", grupo = "Consumo de alcohol y drogas en la vía pública", familia = "Incivilidades", colorHex = "#FFC107", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Porte de drogas", grupo = "Consumo de alcohol y drogas en la vía pública", familia = "Incivilidades", colorHex = "#FFC107", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otras faltas a la ley de drogas", grupo = "Consumo de alcohol y drogas en la vía pública", familia = "Incivilidades", colorHex = "#FFC107", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Disparo injustificado", grupo = "Crímenes y simples delitos ley de armas", familia = "Delitos asociados a armas", colorHex = "#37474F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Porte / posesión de armas o explosivos", grupo = "Crímenes y simples delitos ley de armas", familia = "Delitos asociados a armas", colorHex = "#37474F", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otras infracciones a la ley de armas", grupo = "Crímenes y simples delitos ley de armas", familia = "Delitos asociados a armas", colorHex = "#455A64", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Porte de arma cortante o punzante", grupo = "Porte de arma cortante o punzante", familia = "Delitos asociados a armas", colorHex = "#455A64", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo en lugar habitado", grupo = "Robos en lugar habitado y no habitado", familia = "Delitos contra la propiedad no violentos", colorHex = "#F57C00", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo en lugar no habitado", grupo = "Robos en lugar habitado y no habitado", familia = "Delitos contra la propiedad no violentos", colorHex = "#FB8C00", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo de vehículo motorizado", grupo = "Robos de vehículo motorizado o de sus accesorios", familia = "Delitos contra la propiedad no violentos", colorHex = "#F57C00", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo de objetos de o desde vehículo", grupo = "Robos de vehículo motorizado o de sus accesorios", familia = "Delitos contra la propiedad no violentos", colorHex = "#FB8C00", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otros robos con fuerza en las cosas", grupo = "Otros robos con fuerza en las cosas", familia = "Delitos contra la propiedad no violentos", colorHex = "#FF9800", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Hurtos", grupo = "Hurtos", familia = "Delitos contra la propiedad no violentos", colorHex = "#FF9800", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Receptación", grupo = "Receptación", familia = "Delitos contra la propiedad no violentos", colorHex = "#FF9800", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Amenaza con armas (falta)", grupo = "Amenaza falta o riña", familia = "Incivilidades", colorHex = "#2196F3", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Riña Pública", grupo = "Amenaza falta o riña", familia = "Incivilidades", colorHex = "#2196F3", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Consumo de alcohol en la vía pública", grupo = "Consumo de alcohol y drogas en la vía pública", familia = "Incivilidades", colorHex = "#2196F3", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Daños", grupo = "Daños", familia = "Incivilidades", colorHex = "#1976D2", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Desórdenes públicos", grupo = "Desórdenes públicos", familia = "Incivilidades", colorHex = "#1976D2", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Animales sueltos en la vía pública", grupo = "Otras incivilidades", familia = "Incivilidades", colorHex = "#64B5F6", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Comercio ilegal", grupo = "Otras incivilidades", familia = "Incivilidades", colorHex = "#64B5F6", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Ofensas al pudor", grupo = "Otras incivilidades", familia = "Incivilidades", colorHex = "#64B5F6", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Otras incivilidades", grupo = "Otras incivilidades", familia = "Incivilidades", colorHex = "#64B5F6", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Abigeato", grupo = "Otros delitos o faltas", familia = "Otros delitos o faltas", colorHex = "#546E7A", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
        DelitoTipificado(nombre = "Robo frustrado", grupo = "Otros delitos o faltas", familia = "Otros delitos o faltas", colorHex = "#546E7A", tipoIncidenciaRef = "VdmMNZusszd8HanMFr4S"),
    )

    val listaNombresDeDelitos: List<String> = listaMaestraDeDelitos.map { it.nombre }.sorted()

    fun obtenerDelitoPorNombre(nombreDelito: String): DelitoTipificado? {
        return listaMaestraDeDelitos.find { it.nombre.equals(nombreDelito, ignoreCase = true) }
    }

    fun obtenerDelitosPorGrupo(grupo: String): List<DelitoTipificado> {
        return listaMaestraDeDelitos.filter { it.grupo.equals(grupo, ignoreCase = true) }
    }

    fun obtenerDelitosPorFamilia(familia: String): List<DelitoTipificado> {
        return listaMaestraDeDelitos.filter { it.familia.equals(familia, ignoreCase = true) }
    }

    fun obtenerTodosLosGrupos(): List<String> {
        return listaMaestraDeDelitos.map { it.grupo }.distinct().sorted()
    }

    fun obtenerTodasLasFamilias(): List<String> {
        return listaMaestraDeDelitos.map { it.familia }.distinct().sorted()
    }
}

object TipificacionesDao {
    val listaMaestraDao = listOf(
        // 1. FAMILIA: ASEO
        DelitoTipificado(nombre = "Microbasural en vía pública", grupo = "Gestión de Residuos", familia = "BASURA", colorHex = "#4CAF50", tipoIncidenciaRef = "Xx9dXIOJmX6NQgc2a2kj"),
        DelitoTipificado(nombre = "Escombros no retirados", grupo = "Gestión de Residuos", familia = "BASURA", colorHex = "#4CAF50", tipoIncidenciaRef = "Xx9dXIOJmX6NQgc2a2kj"),
        DelitoTipificado(nombre = "Falla en recolección domiciliaria", grupo = "Servicios Básicos", familia = "BASURA", colorHex = "#4CAF50", tipoIncidenciaRef = "Xx9dXIOJmX6NQgc2a2kj"),
        DelitoTipificado(nombre = "Quema de basura / Residuos", grupo = "Incivilidades Urbanas", familia = "BASURA", colorHex = "#D32F2F", tipoIncidenciaRef = "Xx9dXIOJmX6NQgc2a2kj"),

        // 2. FAMILIA: BACHE
        DelitoTipificado(nombre = "Bache profundo / Socavón", grupo = "Pavimentación", familia = "BACHE", colorHex = "#795548", tipoIncidenciaRef = "3eTWqMVPWYP3imsojTXp"),
        DelitoTipificado(nombre = "Tapa de alcantarillado faltante", grupo = "Emergencia Urbana", familia = "BACHE", colorHex = "#B71C1C", tipoIncidenciaRef = "3eTWqMVPWYP3imsojTXp"),

        // 3. FAMILIA: ILUMINACION
        DelitoTipificado(nombre = "Luminaria apagada / Falla eléctrica", grupo = "Alumbrado Público", familia = "ILUMINACION", colorHex = "#FFEB3B", tipoIncidenciaRef = "65tR5EDkoLz8VKu062Zx"),
        DelitoTipificado(nombre = "Avenida o calle con luminaria apagada / Falla eléctrica", grupo = "Alumbrado Público", familia = "ILUMINACION", colorHex = "#FFEB3B", tipoIncidenciaRef = "65tR5EDkoLz8VKu062Zx"),   
        DelitoTipificado(nombre = "Poste chocado / Riesgo inminente", grupo = "Emergencia Urbana", familia = "ILUMINACION", colorHex = "#B71C1C", tipoIncidenciaRef = "65tR5EDkoLz8VKu062Zx"),
        
        // 4. FAMILIA: SEMAFORO
        DelitoTipificado(nombre = "Semáforo Apagado / Falla eléctrica", grupo = "Alumbrado Público", familia = "SEMAFORO", colorHex = "#FFEB3B", tipoIncidenciaRef = "QfPdJJfAMM6PjMXeivJu"),
        DelitoTipificado(nombre = "Semáforo con luces de emergencia / Falla programación", grupo = "Alumbrado Público", familia = "SEMAFORO", colorHex = "#FFEB3B", tipoIncidenciaRef = "QfPdJJfAMM6PjMXeivJu"),
        DelitoTipificado(nombre = "Semáforo chocado / Riesgo inminente", grupo = "Emergencia Urbana", familia = "SEMAFORO", colorHex = "#B71C1C", tipoIncidenciaRef = "QfPdJJfAMM6PjMXeivJu"),

        // 5. FAMILIA: AUTO_ABANDONADO
        DelitoTipificado(nombre = "Auto Abandonado", grupo = "Auto Abandonado", familia = "AUTO_ABANDONADO", colorHex = "#795548", tipoIncidenciaRef = "kL2Ddyw20OJHsX3iOnNA"),

        // 6. FAMILIA: SALVAVIDAS
        DelitoTipificado(nombre = "Persona Ahogándose / Riesgo Vital", grupo = "Emergencia Playa", familia = "SALVAVIDAS", colorHex = "#FFEB3B", tipoIncidenciaRef = "YXJTANA7o0GGw8OgOHsI"),
        DelitoTipificado(nombre = "Persona bañándose en lugar peligroso", grupo = "Emergencia Playa", familia = "SALVAVIDAS", colorHex = "#B71C1C", tipoIncidenciaRef = "YXJTANA7o0GGw8OgOHsI"),

        // 7. FAMILIA: SOCIAL
        DelitoTipificado(nombre = "Ayuda Social Ciudadana", grupo = "Asistencia Social", familia = "SOCIAL", colorHex = "#9C27B0", tipoIncidenciaRef = "servicio_social_kHMVPITGigENTTpNATWm"),
        DelitoTipificado(nombre = "Entrega de Mercadería / Ayudas", grupo = "Asistencia Social", familia = "SOCIAL", colorHex = "#9C27B0", tipoIncidenciaRef = "servicio_social_kHMVPITGigENTTpNATWm"),

        // 8. FAMILIA: ADULTO_MAYOR
        DelitoTipificado(nombre = "Asistencia Adulto Mayor", grupo = "Cuidado Mayor", familia = "ADULTO_MAYOR", colorHex = "#3F51B5", tipoIncidenciaRef = "seguridadadultomayor_kHMVPITGigENTTpNATWm"),
        DelitoTipificado(nombre = "Denuncia de Maltrato / Abandono", grupo = "Cuidado Mayor", familia = "ADULTO_MAYOR", colorHex = "#D32F2F", tipoIncidenciaRef = "seguridadadultomayor_kHMVPITGigENTTpNATWm"),

        // 9. FAMILIA: EMERGENCIA
        DelitoTipificado(nombre = "Accidente en Carretera", grupo = "S.O.S. Vial", familia = "EMERGENCIA", colorHex = "#F44336", tipoIncidenciaRef = "emergencia_carretera_kHMVPITGigENTTpNATWm"),
        DelitoTipificado(nombre = "Vehículo con Desperfecto", grupo = "S.O.S. Vial", familia = "EMERGENCIA", colorHex = "#FF9800", tipoIncidenciaRef = "emergencia_carretera_kHMVPITGigENTTpNATWm")
    )

    fun obtenerTodasLasFamilias(): List<String> = listaMaestraDao.map { it.familia }.distinct().sorted()
    fun obtenerDelitosPorFamilia(familia: String): List<DelitoTipificado> = listaMaestraDao.filter { it.familia.equals(familia, ignoreCase = true) }

    fun obtenerTodosLosGrupos(): List<String> {
        return listaMaestraDao.map { it.grupo }.distinct().sorted()
    }

    fun obtenerDelitoPorNombre(nombreDelito: String): DelitoTipificado? {
        return listaMaestraDao.find { it.nombre.equals(nombreDelito, ignoreCase = true) }
    }

    fun obtenerDelitosPorGrupo(grupo: String): List<DelitoTipificado> {
        return listaMaestraDao.filter { it.grupo.equals(grupo, ignoreCase = true) }
    }
}

object EstadisticasOficiales2025Repository : HistoricoRepository {
    private val datos2025: Map<String, List<Int>> = mapOf(
        "Homicidios" to listOf(2, 1, 2, 1, 0, 2, 0, 1, 1, 0, 0, 0),
        "Amenazas" to listOf(121, 117, 152, 128, 135, 124, 131, 123, 138, 0, 0, 0),
        "Robos con violencia o intimidación" to listOf(42, 48, 54, 53, 54, 67, 67, 74, 67, 0, 0, 0),
        "Violencia intrafamiliar" to listOf(220, 165, 183, 151, 135, 151, 115, 148, 159, 0, 0, 0),
        "Tráfico de sustancias" to listOf(9, 4, 10, 8, 5, 6, 14, 18, 6, 0, 0, 0),
        "Consumo de alcohol en la vía pública" to listOf(45, 38, 52, 41, 44, 39, 42, 40, 46, 0, 0, 0)
    )

    override fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int {
        val valores = datos2025[nombreDelito] ?: return 0
        return meses.filter { it in 1..12 }.sumOf { valores[it - 1] }
    }
}

object EstadisticasOficiales2024Repository : HistoricoRepository {
    private val datos2024: Map<String, List<Int>> = mapOf(
        "Homicidios" to listOf(1, 2, 1, 0, 1, 3, 1, 2, 1, 2, 1, 1),
        "Amenazas" to listOf(115, 110, 145, 120, 130, 118, 125, 115, 132, 120, 118, 125),
        "Robos con violencia o intimidación" to listOf(38, 42, 50, 48, 50, 60, 62, 68, 60, 55, 52, 58),
        "Violencia intrafamiliar" to listOf(210, 155, 175, 145, 128, 145, 110, 140, 150, 145, 138, 148),
        "Tráfico de sustancias" to listOf(8, 3, 9, 7, 4, 5, 12, 15, 5, 7, 6, 8),
        "Consumo de alcohol en la vía pública" to listOf(42, 35, 48, 38, 40, 36, 39, 37, 42, 38, 36, 40)
    )

    override fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int {
        val valores = datos2024[nombreDelito] ?: return 0
        return meses.filter { it in 1..12 }.sumOf { valores[it - 1] }
    }
}

object EstadisticasOficiales2023Repository : HistoricoRepository {
    private val datos2023: Map<String, List<Int>> = mapOf(
        "Homicidios" to listOf(2, 1, 0, 1, 2, 1, 2, 1, 0, 1, 2, 1),
        "Amenazas" to listOf(105, 100, 135, 110, 120, 108, 115, 105, 122, 110, 108, 115),
        "Robos con violencia o intimidación" to listOf(32, 36, 44, 42, 44, 54, 56, 62, 54, 49, 46, 52),
        "Violencia intrafamiliar" to listOf(195, 140, 160, 130, 113, 130, 95, 125, 135, 130, 123, 133),
        "Tráfico de sustancias" to listOf(6, 2, 7, 5, 3, 4, 10, 13, 4, 6, 5, 7),
        "Consumo de alcohol en la vía pública" to listOf(38, 31, 44, 34, 36, 32, 35, 33, 38, 34, 32, 36)
    )

    override fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int {
        val valores = datos2023[nombreDelito] ?: return 0
        return meses.filter { it in 1..12 }.sumOf { valores[it - 1] }
    }
}

object EstadisticasOficiales2022Repository : HistoricoRepository {
    private val datos2022: Map<String, List<Int>> = mapOf(
        "Homicidios" to listOf(1, 0, 1, 2, 1, 0, 1, 2, 1, 0, 1, 2),
        "Amenazas" to listOf(95, 90, 125, 100, 110, 98, 105, 95, 112, 100, 98, 105),
        "Robos con violencia o intimidación" to listOf(28, 32, 40, 38, 40, 50, 52, 58, 50, 45, 42, 48),
        "Violencia intrafamiliar" to listOf(185, 130, 150, 120, 103, 120, 85, 115, 125, 120, 113, 123),
        "Tráfico de sustancias" to listOf(5, 1, 6, 4, 2, 3, 9, 12, 3, 5, 4, 6),
        "Consumo de alcohol en la vía pública" to listOf(35, 28, 41, 31, 33, 29, 32, 30, 35, 31, 29, 33)
    )

    override fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int {
        val valores = datos2022[nombreDelito] ?: return 0
        return meses.filter { it in 1..12 }.sumOf { valores[it - 1] }
    }
}

object EstadisticasOficiales2021Repository : HistoricoRepository {
    private val datos2021: Map<String, List<Int>> = mapOf(
        "Homicidios" to listOf(0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1),
        "Amenazas" to listOf(85, 80, 115, 90, 100, 88, 95, 85, 102, 90, 88, 95),
        "Robos con violencia o intimidación" to listOf(24, 28, 36, 34, 36, 46, 48, 54, 46, 41, 38, 44),
        "Violencia intrafamiliar" to listOf(175, 120, 140, 110, 93, 110, 75, 105, 115, 110, 103, 113),
        "Tráfico de sustancias" to listOf(4, 0, 5, 3, 1, 2, 8, 11, 2, 4, 3, 5),
        "Consumo de alcohol en la vía pública" to listOf(32, 25, 38, 28, 30, 26, 29, 27, 32, 28, 26, 30)
    )

    override fun obtenerConteoHistorico(nombreDelito: String, meses: IntRange): Int {
        val valores = datos2021[nombreDelito] ?: return 0
        return meses.filter { it in 1..12 }.sumOf { valores[it - 1] }
    }
}
